import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { IRiskCategory } from 'APIPackage/CatalogsModule/interfaces/IRiskCategory';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getCatalogRiskCategories } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type RiskCategoryType = Omit<IRiskCategory, 'id'>;

interface IRiskCategoryTypeEditFormProps {
  onBack: () => void;
}

interface IRiskCategoryTypeEditFormStoreProps {
  riskCategories: IRiskCategory[];
}

const RiskCategoryTypeEditFormWithoutStore = ({
  onBack,
  riskCategories,
}: IRiskCategoryTypeEditFormProps & IRiskCategoryTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedRiskCategoryType, setSelectedRiskCategoryType] = useState<IRiskCategory | undefined>();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const riskCategoryType = riskCategories.find(k => k.id.toString() === id);

      setSelectedRiskCategoryType(prev => (prev?.id === riskCategoryType?.id ? undefined : riskCategoryType));
    },
    [riskCategories],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.RISK_CATEGORIES));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedRiskCategoryType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedRiskCategoryType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: RiskCategoryType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.RISK_CATEGORIES }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedRiskCategoryType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.RISK_CATEGORIES,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedRiskCategoryType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<RiskCategoryType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<RiskCategoryType> onSubmit={onSubmit} initialValues={selectedRiskCategoryType} render={renderForm} />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Категории риска</span>
        {riskCategories &&
          riskCategories.map((riskCategory: IRiskCategory) => (
            <button
              key={riskCategory.id}
              className={selectedRiskCategoryType?.id === riskCategory.id ? styles.DataRowSelected : styles.DataRow}
              data-id={riskCategory.id}
              onClick={onRowClick}
              type="button"
            >
              {riskCategory.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          id="createButton"
          text="Создать"
        />
        {selectedRiskCategoryType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): IRiskCategoryTypeEditFormStoreProps => ({
  riskCategories: getCatalogRiskCategories(state),
});

export const RiskCategoryTypeEditForm = connect(mapStateToProps)(RiskCategoryTypeEditFormWithoutStore);
